/**
 * Lazy official embeds for TikTok videos and X posts inside rendered markdown.
 *
 * Markdown authors write a `<figure data-embed-provider="tiktok|x"
 * data-embed-id="<numeric id>">` with a `[data-embed-slot]` placeholder and a
 * plain-text caption that credits the creator and links to the original post.
 * The caption is part of the SSR HTML, so crawlers and no-JS readers always get
 * the attribution and the link. When the figure scrolls near the viewport, the
 * slot is filled with the platform's own embed iframe. No third-party script is
 * injected and the video file is never re-hosted.
 */

export type SocialEmbedProvider = 'tiktok' | 'x';

const EMBED_ID_PATTERN = /^\d{5,25}$/;
const X_EMBED_ORIGIN = 'https://platform.twitter.com';

export function isSocialEmbedProvider(
  value: string | undefined
): value is SocialEmbedProvider {
  return value === 'tiktok' || value === 'x';
}

/**
 * Builds the official embed URL for a provider and numeric post id.
 * Returns null for anything that is not an allowlisted provider and a numeric
 * id, so markdown can never point the iframe at an arbitrary URL.
 */
export function getSocialEmbedSrc(
  provider: string | undefined,
  id: string | undefined,
  options: { dark?: boolean } = {}
): string | null {
  if (!isSocialEmbedProvider(provider) || !id || !EMBED_ID_PATTERN.test(id)) {
    return null;
  }
  if (provider === 'tiktok') {
    return `https://www.tiktok.com/embed/v2/${id}`;
  }
  const params = new URLSearchParams({
    id,
    dnt: 'true',
    theme: options.dark ? 'dark' : 'light',
  });
  return `${X_EMBED_ORIGIN}/embed/Tweet.html?${params.toString()}`;
}

const INITIAL_HEIGHT: Record<SocialEmbedProvider, number> = {
  tiktok: 740,
  x: 640,
};

function mountEmbed(figure: HTMLElement) {
  if (figure.dataset.embedMounted === 'true') return;
  const provider = figure.dataset.embedProvider;
  const id = figure.dataset.embedId;
  const dark = document.documentElement.classList.contains('dark');
  const src = getSocialEmbedSrc(provider, id, { dark });
  const slot = figure.querySelector<HTMLElement>('[data-embed-slot]');
  if (!src || !slot || !isSocialEmbedProvider(provider)) return;

  const iframe = document.createElement('iframe');
  iframe.src = src;
  iframe.title =
    figure.dataset.embedTitle ??
    (provider === 'tiktok' ? 'TikTok video' : 'Post on X');
  iframe.loading = 'lazy';
  iframe.allow = 'encrypted-media; fullscreen; picture-in-picture';
  iframe.referrerPolicy = 'strict-origin-when-cross-origin';
  iframe.style.height = `${INITIAL_HEIGHT[provider]}px`;
  iframe.className = 'social-embed-iframe';
  if (provider === 'x') iframe.dataset.embedXId = id;

  slot.replaceChildren(iframe);
  figure.dataset.embedMounted = 'true';
}

/** Resizes X post iframes from the height messages the X embed page posts. */
function handleXResize(container: HTMLElement, event: MessageEvent) {
  if (event.origin !== X_EMBED_ORIGIN) return;
  let data: unknown = event.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return;
    }
  }
  const embed = (data as { 'twttr.embed'?: unknown })?.['twttr.embed'] as
    | { method?: string; params?: Array<{ height?: number }> }
    | undefined;
  const height = embed?.params?.[0]?.height;
  if (embed?.method !== 'twttr.private.resize' || typeof height !== 'number') {
    return;
  }
  for (const iframe of container.querySelectorAll<HTMLIFrameElement>(
    'iframe[data-embed-x-id]'
  )) {
    if (iframe.contentWindow === event.source) {
      iframe.style.height = `${Math.ceil(height)}px`;
    }
  }
}

/**
 * Wires lazy embeds for every embed figure inside `container`.
 * Returns a cleanup function.
 */
export function setupSocialEmbeds(container: HTMLElement): () => void {
  const figures = Array.from(
    container.querySelectorAll<HTMLElement>('figure[data-embed-provider]')
  );
  if (figures.length === 0) return () => {};

  const onMessage = (event: MessageEvent) => handleXResize(container, event);
  window.addEventListener('message', onMessage);

  if (typeof IntersectionObserver === 'undefined') {
    for (const figure of figures) mountEmbed(figure);
    return () => window.removeEventListener('message', onMessage);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        mountEmbed(entry.target as HTMLElement);
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '400px 0px' }
  );
  for (const figure of figures) observer.observe(figure);

  return () => {
    observer.disconnect();
    window.removeEventListener('message', onMessage);
  };
}
